/* Attachments, images, and the four one-off controls that finish the set.
 *
 * Between them these are the last field types that block a form from
 * rendering. Attach and Attach Image are the ones that matter -- a bill
 * without its scan is an argument waiting to happen -- and the rest
 * (Signature, Barcode, Geolocation, Icon) block one or two forms each and are
 * built because leaving them out would mean the claim "every form draws" came
 * with a footnote.
 */
import { useRef, useState } from 'react'
import type { Control } from './kit'
import { Field, asText } from './kit'
import { useErp } from '../context'

/** Attach and Attach Image. Frappe stores the file's URL in the field and
 *  keeps a File record beside the document. */
export function makeAttach(kind: 'file' | 'image'): Control {
  return function AttachControl(p) {
    const erp = useErp()
    const input = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [failed, setFailed] = useState<string | null>(null)
    const url = asText(p.value)
    const docname = asText((p.doc as Record<string, unknown>).name)

    const choose = async (file: File) => {
      setBusy(true); setFailed(null)
      try {
        const body = new FormData()
        body.append('file', file)
        body.append('doctype', asText((p.doc as Record<string, unknown>).doctype) || '')
        body.append('name', docname)
        body.append('fieldname', p.field.fieldname)
        const record = await erp.upload(body)
        p.onChange(record.file_url ?? null)
      } catch (e) {
        setFailed((e as Error).message)
      } finally {
        setBusy(false)
        if (input.current) input.current.value = ''
      }
    }

    // Frappe attaches a File to an existing document, so there is nothing to
    // attach it to until the document has been saved once. Saying so beats a
    // silent failure at the moment somebody picks a file.
    if (!docname) {
      return (
        <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
          <div className="input" style={{ display: 'flex', alignItems: 'center', background: 'var(--sunk)', color: 'var(--ink-3)' }}>
            Save first, then attach
          </div>
        </Field>
      )
    }

    return (
      <Field field={p.field} required={p.required} error={p.error || failed || undefined} bare={p.bare}>
        <div className="stack" style={{ gap: 8 }}>
          {url ? (
            <div className="row" style={{ gap: 8 }}>
              {kind === 'image' ? (
                <img src={url} alt={p.field.label || 'Attachment'} loading="lazy"
                     style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }} />
              ) : <Paperclip />}
              <a className="grow truncate small" href={url} target="_blank" rel="noreferrer">
                {url.split('/').pop()}
              </a>
              {p.readOnly ? null : (
                <button type="button" className="btn btn-quiet btn-sm btn-danger"
                        onClick={() => p.onChange(null)}
                        aria-label={`Remove ${p.field.label ?? 'attachment'}`}>Remove</button>
              )}
            </div>
          ) : null}
          {p.readOnly ? (url ? null : <span className="small muted">Nothing attached</span>) : (
            <>
              <input ref={input} type="file" className="sr-only" id={`att_${p.field.fieldname}`}
                     accept={kind === 'image' ? 'image/*' : undefined}
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f) }} />
              <label className="btn btn-sm" htmlFor={`att_${p.field.fieldname}`}
                     style={{ width: 'fit-content', cursor: busy ? 'progress' : 'pointer' }}>
                {busy ? 'Uploading…' : url ? 'Replace' : kind === 'image' ? 'Choose an image' : 'Choose a file'}
              </label>
            </>
          )}
        </div>
      </Field>
    )
  }
}

/** A read-only picture: the item photo, the company logo. */
export const ImageControl: Control = (p) => {
  const url = asText(p.value)
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      {url
        ? <img src={url} alt={p.field.label || ''} loading="lazy"
               style={{ maxWidth: 180, maxHeight: 180, borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }} />
        : <span className="small muted">No image</span>}
    </Field>
  )
}

/** Chips backed by a child table of links. */
export function makeMultiSelect(): Control {
  return function TableMultiSelect(p) {
    const erp = useErp()
    const rows = Array.isArray(p.value) ? (p.value as Record<string, unknown>[]) : []
    const [text, setText] = useState('')
    const [options, setOptions] = useState<{ value: string; label?: string }[]>([])
    const [open, setOpen] = useState(false)

    // The child table's link field is the one that is not bookkeeping. Frappe
    // names it after the target most of the time, so the first Link field in
    // the child is the right one and this stays out of its way.
    const linkField = (p.field.options && 'name') || 'name'
    const target = p.field.options ?? ''

    const search = async (value: string) => {
      setText(value); setOpen(true)
      try { setOptions((await erp.searchLink(target, value)).results ?? []) } catch { setOptions([]) }
    }
    const add = (value: string) => {
      if (rows.some((r) => Object.values(r).includes(value))) return
      p.onChange([...rows, { [linkField]: value, __islocal: 1 }])
      setText(''); setOpen(false)
    }

    return (
      <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
        <div className="card" style={{ padding: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', position: 'relative' }}>
          {rows.map((row, i) => {
            const label = String(row[linkField] ?? Object.values(row)[0] ?? '')
            return (
              <span key={label + i} className="chip chip-info">
                {label}
                {p.readOnly ? null : (
                  <button type="button" className="btn btn-quiet btn-sm" aria-label={`Remove ${label}`}
                          style={{ minHeight: 0, padding: '0 2px' }}
                          onClick={() => p.onChange(rows.filter((_, j) => j !== i))}>×</button>
                )}
              </span>
            )
          })}
          {p.readOnly ? null : (
            <input className="grow" style={{ border: 0, background: 'transparent', color: 'inherit', font: 'inherit', minWidth: 120, outline: 'none', minHeight: 28 }}
                   value={text} placeholder={rows.length ? 'Add another…' : `Search ${target}…`}
                   onChange={(e) => void search(e.target.value)}
                   onFocus={() => void search(text)}
                   onBlur={() => window.setTimeout(() => setOpen(false), 120)} />
          )}
          {open && options.length ? (
            <div className="card" style={{ position: 'absolute', top: '100%', insetInline: 0, zIndex: 30, padding: 4, boxShadow: 'var(--shadow-2)', maxHeight: 230, overflowY: 'auto' }}>
              {options.map((o) => (
                <button type="button" key={o.value} className="navitem"
                        onMouseDown={(e) => { e.preventDefault(); add(o.value) }}>
                  {o.label || o.value}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </Field>
    )
  }
}

export const SignatureControl: Control = (p) => {
  const canvas = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const value = asText(p.value)

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    return [e.clientX - box.left, e.clientY - box.top] as const
  }
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (p.readOnly) return
    drawing.current = true
    const ctx = canvas.current!.getContext('2d')!
    ctx.strokeStyle = getComputedStyle(canvas.current!).color
    ctx.lineWidth = 2; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(...point(e))
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const ctx = canvas.current!.getContext('2d')!
    ctx.lineTo(...point(e)); ctx.stroke()
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    p.onChange(canvas.current!.toDataURL('image/png'))
  }

  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      <div className="stack" style={{ gap: 6 }}>
        {value && p.readOnly
          ? <img src={value} alt="Signature" style={{ maxHeight: 90, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }} />
          : <canvas ref={canvas} width={420} height={120}
                    onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
                    aria-label={`${p.field.label || 'Signature'} — draw to sign`}
                    style={{ width: '100%', maxWidth: 420, height: 120, touchAction: 'none', cursor: p.readOnly ? 'default' : 'crosshair',
                             border: '1px solid var(--line-strong)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink)' }} />}
        {p.readOnly ? null : (
          <button type="button" className="btn btn-sm" style={{ width: 'fit-content' }}
                  onClick={() => {
                    const ctx = canvas.current?.getContext('2d')
                    if (ctx && canvas.current) ctx.clearRect(0, 0, canvas.current.width, canvas.current.height)
                    p.onChange(null)
                  }}>Clear</button>
        )}
      </div>
    </Field>
  )
}

/** Shown, not scanned. Reading one needs a camera and a permission prompt,
 *  which is its own feature rather than a field control. */
export const BarcodeControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <input className="input mono" value={asText(p.value)} readOnly={p.readOnly}
           inputMode="numeric" autoComplete="off"
           onChange={(e) => p.onChange(e.target.value)} />
  </Field>
)

/** One field in the whole surface uses this. Latitude and longitude, typed,
 *  with a link out — a map inside a form field is ERP-21's problem. */
export const GeolocationControl: Control = (p) => {
  const raw = asText(p.value)
  let point: [number, number] | null = null
  try {
    const parsed = JSON.parse(raw)
    const coords = parsed?.features?.[0]?.geometry?.coordinates
    if (Array.isArray(coords) && coords.length >= 2) point = [Number(coords[1]), Number(coords[0])]
  } catch { /* not GeoJSON: show it as text */ }
  return (
    <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
      {point ? (
        <div className="row">
          <span className="mono small grow">{point[0].toFixed(5)}, {point[1].toFixed(5)}</span>
          <a className="btn btn-sm" target="_blank" rel="noreferrer"
             href={`https://www.openstreetmap.org/?mlat=${point[0]}&mlon=${point[1]}#map=15/${point[0]}/${point[1]}`}>
            Open map
          </a>
        </div>
      ) : (
        <textarea className="textarea mono" rows={2} value={raw} readOnly={p.readOnly}
                  placeholder="GeoJSON" onChange={(e) => p.onChange(e.target.value)} />
      )}
    </Field>
  )
}

/** A Frappe icon name. Free text with the value echoed, because the icon set
 *  is Frappe's sprite sheet and this app does not load it. */
export const IconControl: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <input className="input mono" value={asText(p.value)} readOnly={p.readOnly}
           placeholder="icon name" onChange={(e) => p.onChange(e.target.value)} />
  </Field>
)

const Paperclip = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" style={{ color: 'var(--ink-3)', flex: 'none' }}>
    <path d="M10.5 5.5 6 10a1.8 1.8 0 0 0 2.5 2.5l4.5-4.5a3.4 3.4 0 0 0-4.8-4.8L3.5 7.7a5 5 0 0 0 7 7l3.5-3.5" strokeLinecap="round" />
  </svg>
)

/** An `HTML` field is a block of markup the DocType carries in `options`:
 *  a note, a legend, a bit of formatting between sections.
 *
 *  It is admin-authored, and an admin who wanted to run code could write a
 *  Server Script instead — but "they could do worse elsewhere" is not a
 *  reason to hand a string to `innerHTML`. Tags that execute are stripped,
 *  and so is every `on*` attribute and `javascript:` URL.
 */
export const HtmlControl: Control = (p) => {
  const markup = p.field.options ?? ''
  if (!markup.trim()) return null
  return (
    <div className="small" style={{ color: 'var(--ink-2)' }}
         dangerouslySetInnerHTML={{ __html: sanitise(markup) }} />
  )
}

const DANGEROUS = /<\/?(script|style|iframe|object|embed|link|meta|form|base)\b[^>]*>/gi

function sanitise(markup: string): string {
  return markup
    .replace(DANGEROUS, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/(href|src)\s*=\s*["']?\s*javascript:[^"'\s>]*/gi, '$1="#"')
}
